"use client";
import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, useInView } from "framer-motion";
import Image, { ImageProps } from "next/image";

interface CarouselProps {
  items: React.ReactElement[];
  initialScroll?: number;
  startPadding?: string;
}

type Card = {
  src: string;
  title: string;
  category: string;
  description?: string;
};

const cardEnterVariant = {
  initial: { opacity: 0, y: 20 },
  animate: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: 0.2 * index, ease: "easeOut" },
  }),
};

export const Carousel = ({ items, initialScroll = 0, startPadding = "pl-4" }: CarouselProps) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.2 });
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScrollability = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
    }
  };

  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = initialScroll;
      checkScrollability();
    }
  }, [initialScroll]);

  const scrollLeft = () => {
    carouselRef.current?.scrollBy({ left: -300, behavior: "smooth" });
  };

  const scrollRight = () => {
    carouselRef.current?.scrollBy({ left: 300, behavior: "smooth" });
  };

  return (
    <div className="relative w-full">
      <div
        className="flex w-full overflow-x-scroll overscroll-x-auto scroll-smooth py-10 [scrollbar-width:none] md:py-20"
        ref={carouselRef}
        onScroll={checkScrollability}
      >
        <div className={cn("absolute right-0 z-[1000] h-auto w-[5%] overflow-hidden bg-gradient-to-l")} />

        <div ref={containerRef} className={cn("flex flex-row justify-start gap-4 mx-auto", startPadding)}>
          {items.map((item, index) => (
            <motion.div
              key={`card-${index}`}
              custom={index}
              variants={cardEnterVariant}
              initial="initial"
              animate={isInView ? "animate" : "initial"}
              className="rounded-3xl last:pr-4"
            >
              {item}
            </motion.div>
          ))}
        </div>
      </div>
      <div className="mr-10 flex justify-end gap-2">
        <button
          className="relative z-40 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 disabled:opacity-50"
          onClick={scrollLeft}
          disabled={!canScrollLeft}
          aria-label="Scroll left"
        >
          <ArrowLeft className="h-6 w-6 text-gray-500" aria-hidden="true" />
        </button>
        <button
          className="relative z-40 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 disabled:opacity-50"
          onClick={scrollRight}
          disabled={!canScrollRight}
          aria-label="Scroll right"
        >
          <ArrowRight className="h-6 w-6 text-gray-500" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export const Card = ({ card }: { card: Card }) => {
  return (
    <div className="relative z-10 flex h-[22rem] w-[58vw] max-w-[220px] flex-col items-start justify-start overflow-hidden rounded-xl bg-gray-100 sm:h-[267px] sm:max-w-none sm:w-[187px] md:h-[33rem] md:w-80 dark:bg-neutral-900">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-30 h-full bg-gradient-to-b from-blue-900 via-transparent to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 h-full bg-gradient-to-t from-blue-900 to-transparent" />
      <div className="relative z-40 p-4 sm:p-6 md:p-8">
        <p className="text-left inline-block py-2 px-2 sm:px-3 rounded-sm bg-blue-900 uppercase font-extrabold text-xs sm:text-sm text-blue-200 md:text-base">
          {card.category}
        </p>
        <div className="mt-2 sm:mt-4 max-w-xs text-left font-sans leading-[1.2] font-light [text-wrap:balance] text-white text-2xl md:text-3xl [&>*]:font-[inherit] [&_strong]:font-semibold" dangerouslySetInnerHTML={{ __html: card.title }} />
      </div>
      {card.description ? (
        <div className="absolute bottom-0 left-0 right-0 z-40 p-4 sm:p-6 md:p-8">
          <div className="text-left font-sans text-gray-100 text-sm md:text-base [&_strong]:font-extrabold [&_strong]:text-white" dangerouslySetInnerHTML={{ __html: card.description }} />
        </div>
      ) : null}
      <BlurImage
        src={card.src}
        alt={card.title}
        fill
        className="absolute inset-0 z-10 object-cover opacity-20"
      />
    </div>
  );
};

export const BlurImage = ({ height, width, src, className, alt, ...rest }: ImageProps) => {
  const [isLoading, setLoading] = useState(true);

  if (!src) return null;
  return (
    <Image
      src={src}
      alt={alt ?? "Background of a beautiful view"}
      height={height}
      width={width}
      className={cn(
        "h-full w-full transition duration-300",
        isLoading ? "blur-sm" : "blur-0",
        className,
      )}
      onLoad={() => setLoading(false)}
      {...rest}
    />
  );
};
